import { ExternalLink, Info } from 'lucide-react'
import { cn } from '../../lib/cn'
import { steamStoreUrl } from '../../lib/format'
import type { Recommendation, TogethernessMode } from '../../lib/types'
import { Card, GameArt } from '../ui'
import { GiftableBadge } from './GiftableBadge'
import { MetaChips } from './MetaChips'
import { SlotBadge } from './SlotBadge'
import { FeedbackControls, PlayingThisButton } from './FeedbackControls'
import { presentResultCopy } from './humanize'

interface ResultCardProps {
  rec: Recommendation
  rosterSize?: number
  /** Session togetherness — grounds the synthesized why-line's vibe phrase. */
  togethernessMode?: TogethernessMode | null
  compact?: boolean
  onOpenDetail?: (appid: number) => void
}

const lowConfidence = (rec: Recommendation) => rec.quality_verdict === 'uncertain' || rec.is_exploration

export function ResultCard({
  rec,
  rosterSize,
  togethernessMode,
  compact = false,
  onOpenDetail,
}: ResultCardProps) {
  const isTopPick = rec.slot_type === 'primary' && rec.rank === 1
  // §6.5/§1.4: exactly ONE "Tonight's pick" — the primary label + glow belong
  // to rank 1 only. Runners-up stay calm even when the payload marks every
  // slot primary; non-primary slots (spotlight/wildcard) label themselves
  // wherever they appear.
  const showSlotBadge = rec.slot_type !== 'primary' || isTopPick
  const gamble = lowConfidence(rec)
  const { why, reasons } = presentResultCopy(rec, togethernessMode)

  return (
    <Card elevation="raised" glow={isTopPick} className={cn('space-y-4', compact && 'p-4')}>
      {(showSlotBadge || gamble || rec.giftable) && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {showSlotBadge && <SlotBadge slotType={rec.slot_type} isExploration={rec.is_exploration} />}
            {/* §3.7 giftable annotation. The feed payload carries member ids only
                (no display names) → nameless label. Non-interactive: the session
                envelope exposes no group id to navigate to. */}
            {rec.giftable && (
              <GiftableBadge missingName={null} proposed={rec.giftable.purchase_id != null} />
            )}
          </div>
          {gamble && <span className="text-xs text-warning-fg">A bit of a gamble, but worth a look.</span>}
        </div>
      )}

      {/* Header grid: at phone widths the chip row escapes the narrow column
          beside the art and flows full-width below it — one-line pills would
          otherwise overflow the card; from sm: it sits beside the art (§6.2). */}
      <div className="grid grid-cols-[auto_1fr] items-start gap-x-4 gap-y-2">
        <GameArt
          appid={rec.appid}
          src={rec.header_image}
          name={rec.name}
          className={cn('shrink-0 sm:row-span-2', compact ? 'h-16 w-32' : 'h-20 w-40')}
        />
        <h3
          className={cn(
            'min-w-0 self-center font-semibold leading-snug text-text-strong sm:self-start',
            compact ? 'text-base' : 'text-lg',
          )}
        >
          {/* Render only the grounded title the API returns, keyed by appid. */}
          {rec.name ?? `App ${rec.appid}`}
        </h3>
        <div className="col-span-2 min-w-0 sm:col-span-1">
          <MetaChips rec={rec} rosterSize={rosterSize} />
        </div>
      </div>

      {/* The game's own IGDB summary — the primary "what is this game" text
          (Steam-style). Grounded copy from the API; omitted when absent. */}
      {rec.description && (
        <p className="line-clamp-3 max-w-prose text-sm leading-relaxed text-text-muted">
          {rec.description}
        </p>
      )}

      {/* The "why" is primary content, not a tooltip (skill guardrail #5) —
          humanized to player voice (§5.2), never raw rubric text. */}
      {why && <p className="max-w-prose text-sm leading-relaxed text-text">{why}</p>}

      {/* Curated per-module evidence (top 2–3, humanized) — the determinism /
          trust spine without the raw-signal firehose (§1.6 restraint). */}
      {!compact && reasons.length > 0 && (
        <ul className="space-y-1.5 border-l-2 border-border-warm pl-3">
          {reasons.map((r, i) => (
            <li key={`${r.label}-${i}`} className="flex items-start gap-1.5 text-xs text-text-muted">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-faint" strokeWidth={1.75} aria-hidden="true" />
              <span>
                {r.label && <span className="font-medium text-text">{r.label}: </span>}
                {r.text}
              </span>
            </li>
          ))}
        </ul>
      )}

      <FeedbackControls recommendationId={rec.id} />

      {/* Footer (§6.2): quiet secondary links + the ONE primary action. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <div className="flex items-center gap-3">
          {onOpenDetail && (
            <button
              type="button"
              onClick={() => onOpenDetail(rec.appid)}
              className="rounded-control text-sm font-medium text-text-muted transition duration-200 ease-out hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
            >
              Details
            </button>
          )}
          <a
            href={steamStoreUrl(rec.appid)}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 rounded-control text-sm text-text-muted transition duration-200 ease-out hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Steam <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            <span className="sr-only"> (opens in new tab)</span>
          </a>
        </div>
        <PlayingThisButton recommendationId={rec.id} />
      </div>
    </Card>
  )
}
